import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import {
  Compass, Target, Eye, HeartHandshake, BookOpen, ShieldCheck, FileText,
  Phone, Truck, Users, ClipboardList, ExternalLink, Megaphone, ArrowRight,
  Building2, Route as RouteIcon, Lock, History, ClipboardCheck,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ---- Admin-only operational readiness references (single source of truth; paths, not copies) ----
const READINESS_DOCS = [
  { name: 'Executive Summary', path: 'memory/PHASE2_READINESS/PHASE2_00_EXECUTIVE_SUMMARY.md' },
  { name: 'Operational Readiness Report', path: 'memory/PHASE2_READINESS/PHASE2_01_OPERATIONAL_READINESS_REPORT.md' },
  { name: 'Portal Walkthrough Report', path: 'memory/PHASE2_READINESS/PHASE2_02_PORTAL_WALKTHROUGH_REPORT.md' },
  { name: 'Day-One Launch Checklist', path: 'memory/PHASE2_READINESS/PHASE2_03_DAY_ONE_LAUNCH_CHECKLIST.md' },
  { name: 'Business Readiness Assessment', path: 'memory/PHASE2_READINESS/PHASE2_04_BUSINESS_READINESS_ASSESSMENT.md' },
  { name: 'Job Postings Review', path: 'memory/PHASE2_READINESS/PHASE2_05_JOB_POSTINGS_REVIEW.md' },
  { name: 'Operational Recommendations', path: 'memory/PHASE2_READINESS/PHASE2_06_OPERATIONAL_RECOMMENDATIONS.md' },
];

// ---- Current, approved & operational services (data-driven for future readiness) ----
const CURRENT_SERVICES = [
  { name: 'Lease-On Owner Operators', desc: 'Partner your authority/equipment with BlueLock dispatch and settlement support.', to: '/lease-on', icon: Truck },
  { name: 'Company Drivers', desc: 'Drive company equipment under organized dispatch and transparent pay.', to: '/company-driver', icon: Users },
  { name: 'Dispatch-Only Services', desc: 'Professional dispatch coordination for owner-operators.', to: '/dispatch-services', icon: RouteIcon },
];

// ---- Approved capabilities that are governed & internally quotable but NOT yet publicly launched. ----
// Moving Services (MOV-01/MOV-02) are active & quotable in the internal Quote Builder per BLA-STD-007,
// so they are NOT "planned/unavailable" — but they are not a publicly launched operational service yet.
const APPROVED_NOT_LAUNCHED = [
  { name: 'Moving Services', desc: 'Approved & internally quotable (BLA-STD-007: MOV-01 / MOV-02). Not yet publicly launched.' },
];

// ---- Approved future BUSINESS services ONLY (customer-facing freight capabilities). ----
const FUTURE_ROADMAP = [
  'White Glove', 'Courier', 'Final Mile', 'Driveaway',
  'Brokerage', 'Warehousing', 'RateIQ',
];

// ---- Enterprise Intelligence & Governance infrastructure (NOT customer freight services). ----
// XJAB retains its own governed identity; Enterprise Intelligence Foundation is the provider-neutral
// enduring foundation (formerly labeled "Neo Foundation"); Neo is the implementation agent, not the
// permanent architectural identity. These are distinct concepts and are not equivalent.
const ENTERPRISE_INTELLIGENCE = [
  { name: 'XJAB — Governed Knowledge & Evidence', note: 'Founder-approved authoritative knowledge, provenance & evidence tiers (KDI-001).' },
  { name: 'Enterprise Intelligence Foundation', note: 'Provider-neutral enduring intelligence foundation for BlueLock.' },
  { name: 'Enterprise Platform', note: 'Underlying enterprise operating infrastructure.' },
];

// ---- Company resources: LINK to existing single-source docs (no duplication) ----
const RESOURCES = [
  { name: 'About BlueLock', to: '/about', icon: Building2, external: false },
  { name: 'Programs Overview', to: '/programs', icon: ClipboardList, external: false },
  { name: 'Policies & Compliance', to: '/policies', icon: ShieldCheck, external: false },
  { name: 'Safety Information', to: '/policies', icon: ShieldCheck, external: false },
  { name: 'FAQ', to: '/faq', icon: BookOpen, external: false },
  { name: 'Privacy Policy', to: '/policies', icon: FileText, external: false },
  { name: 'Terms of Service', to: '/policies', icon: FileText, external: false },
  { name: 'Contact BlueLock', to: '/contact', icon: Phone, external: false },
];

// ---- Ecosystem navigation: one connected experience ----
const ECOSYSTEM = [
  { name: 'Main Website', to: '/', icon: Building2 },
  { name: 'Driver Portal', to: '/dashboard', icon: Truck },
  { name: 'Apply to Drive', to: '/apply', icon: ClipboardList },
  { name: 'Request a Quote', to: '/contact', icon: FileText },
  { name: 'Contact BlueLock', to: '/contact', icon: Phone },
];

const CORE_VALUES = [
  { title: 'Compliance First', desc: 'We operate to the letter of FMCSA and state regulation—safety and legitimacy are non-negotiable.' },
  { title: 'Transparency', desc: 'Clear settlements, clear expectations, and honest communication with every driver and partner.' },
  { title: 'Long-Term Partnership', desc: 'We build durable relationships with drivers, not transactions.' },
  { title: 'Operational Discipline', desc: 'Organized dispatch, dependable systems, and consistent execution.' },
];

const SectionCard = ({ children, className = '', ...rest }) => (
  <section className={`bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 ${className}`} {...rest}>{children}</section>
);

const BlueLockHubPage = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'ops_admin', 'super_admin'].includes(user?.role);
  const [announcements, setAnnouncements] = useState([]);
  const [timeline, setTimeline] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    fetch(`${API_URL}/api/communications/feed`, { headers })
      .then((r) => (r.ok ? r.json() : { communications: [] }))
      .then((d) => setAnnouncements(d.communications || []))
      .catch(() => setAnnouncements([]));
    fetch(`${API_URL}/api/communications/timeline`, { headers })
      .then((r) => (r.ok ? r.json() : { timeline: [] }))
      .then((d) => setTimeline(d.timeline || []))
      .catch(() => setTimeline([]));
  }, []);

  const fmtType = (t) => (t || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="bluelock-hub">
        {/* ===== 1. Welcome ===== */}
        <div className="relative overflow-hidden rounded-2xl bg-blue-900 text-white p-8 sm:p-12" data-testid="hub-welcome">
          <div className="relative z-10 max-w-3xl">
            <div className="inline-flex items-center gap-2 text-blue-200 text-sm font-medium mb-3">
              <Compass className="w-4 h-4" /> BlueLock Hub — Company Front Door
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">Welcome to BlueLock</h1>
            <p className="text-blue-100 text-base sm:text-lg leading-relaxed">
              BlueLock Freights is the trade name of <span className="font-semibold text-white">BlueLock Transport LLC</span>, a
              Michigan-based motor carrier (USDOT 4557330 · MC 1810883) headquartered in Grandville, MI. We move freight through
              organized dispatch, transparent settlements, and long-term driver partnerships.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-blue-700/60 px-4 py-2 text-sm font-semibold">
              <HeartHandshake className="w-4 h-4" /> Drive With Purpose
            </div>
          </div>
          <Truck className="absolute right-6 -bottom-4 w-48 h-48 text-blue-800/40 hidden sm:block" />
        </div>

        {/* Mission / Vision */}
        <div className="grid gap-6 md:grid-cols-2">
          <SectionCard data-testid="hub-mission">
            <div className="flex items-center gap-3 mb-3">
              <Target className="w-6 h-6 text-blue-600" />
              <h2 className="text-lg font-bold text-gray-900">Our Mission</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              To move freight reliably and compliantly while giving drivers a professional, transparent, and dependable home—
              built on organized dispatch, fair settlements, and genuine partnership.
            </p>
          </SectionCard>
          <SectionCard data-testid="hub-vision">
            <div className="flex items-center gap-3 mb-3">
              <Eye className="w-6 h-6 text-blue-600" />
              <h2 className="text-lg font-bold text-gray-900">Our Vision</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              To become a trusted, respected carrier known for doing things the right way—proving operational excellence with
              BlueLock first, then thoughtfully expanding into new services as we earn the right to grow.
            </p>
          </SectionCard>
        </div>

        {/* Core Values */}
        <SectionCard data-testid="hub-values">
          <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-3">
            <HeartHandshake className="w-6 h-6 text-blue-600" /> Core Values
          </h2>
          <div className="grid gap-5 sm:grid-cols-2">
            {CORE_VALUES.map((v) => (
              <div key={v.title} className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-semibold text-gray-900">{v.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{v.desc}</p>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* Company Story / Why we exist */}
        <SectionCard data-testid="hub-story">
          <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-blue-600" /> Our Story &amp; Why BlueLock Exists
          </h2>
          <p className="text-gray-600 leading-relaxed">
            BlueLock was founded on a simple conviction: trucking works best when it is run with discipline and integrity.
            Too many drivers experience unclear pay, disorganized dispatch, and shortcuts on compliance. BlueLock exists to be
            the opposite—a carrier where regulatory compliance, transparent settlements, and secure internal systems come first,
            and where owner-operators, lease-on drivers, and company drivers are treated as long-term partners. We build for
            today's operation and design for tomorrow's growth.
          </p>
        </SectionCard>

        {/* ===== 2. Current Services ===== */}
        <SectionCard data-testid="hub-current-services">
          <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-3">
            <Truck className="w-6 h-6 text-blue-600" /> Current Services
          </h2>
          <p className="text-sm text-gray-500 mb-5">Driver programs accepting participants today.</p>
          <div className="grid gap-5 sm:grid-cols-3">
            {CURRENT_SERVICES.map((s) => (
              <Link key={s.name} to={s.to} data-testid={`hub-service-${s.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className="group rounded-xl border border-gray-200 p-5 hover:border-blue-400 hover:shadow-md transition-all">
                <s.icon className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="font-semibold text-gray-900 flex items-center gap-1">
                  {s.name} <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </h3>
                <p className="text-sm text-gray-600 mt-1">{s.desc}</p>
              </Link>
            ))}
          </div>
        </SectionCard>

        {/* ===== 2b. Approved capabilities — not yet publicly launched ===== */}
        <SectionCard data-testid="hub-approved-capabilities">
          <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-3">
            <ShieldCheck className="w-6 h-6 text-indigo-600" /> Approved Capabilities
          </h2>
          <p className="text-sm text-gray-500 mb-5">Governed and available internally; not yet launched as a public operational service.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {APPROVED_NOT_LAUNCHED.map((s) => (
              <div key={s.name} data-testid={`hub-approved-${s.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className="rounded-lg border border-indigo-200 bg-indigo-50/50 px-4 py-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-gray-900">{s.name}</span>
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 bg-indigo-100 px-2 py-1 rounded-full">Approved · Internal quoting only</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{s.desc}</p>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* ===== 3. Future Roadmap ===== */}
        <SectionCard data-testid="hub-future-roadmap" className="border-dashed">
          <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-3">
            <RouteIcon className="w-6 h-6 text-amber-500" /> BlueLock Roadmap — Future Services
          </h2>
          <p className="text-sm text-gray-500 mb-5">Approved future business/service directions. Structure prepared; not yet enabled.</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {FUTURE_ROADMAP.map((f) => (
              <div key={f} data-testid={`hub-future-${f.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className="flex items-center justify-between rounded-lg bg-gray-50 border border-gray-200 px-4 py-3">
                <span className="font-medium text-gray-700">{f}</span>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-100 px-2 py-1 rounded-full">
                  <Lock className="w-3 h-3" /> Planned — Not Currently Available
                </span>
              </div>
            ))}
          </div>

          <h3 className="text-sm font-bold text-gray-900 mt-6 mb-1 flex items-center gap-2" data-testid="hub-enterprise-intelligence">
            <ShieldCheck className="w-5 h-5 text-indigo-600" /> Enterprise Intelligence &amp; Governance
          </h3>
          <p className="text-xs text-gray-500 mb-3">Internal governed intelligence infrastructure — distinct from customer freight services. These are separate concepts and are not interchangeable.</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {ENTERPRISE_INTELLIGENCE.map((e) => (
              <div key={e.name} data-testid={`hub-ei-${e.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className="rounded-lg bg-indigo-50/50 border border-indigo-100 px-4 py-3">
                <p className="font-medium text-indigo-900 text-sm">{e.name}</p>
                <p className="text-[11px] text-gray-500 mt-0.5">{e.note}</p>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* ===== 4. Company Resources ===== */}
        <SectionCard data-testid="hub-resources">
          <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-blue-600" /> Company Resources
          </h2>
          <p className="text-sm text-gray-500 mb-5">Single source of truth—links to existing company documentation.</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {RESOURCES.map((r) => (
              <Link key={r.name} to={r.to} data-testid={`hub-resource-${r.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className="flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 hover:border-blue-400 hover:bg-blue-50 transition-colors">
                <r.icon className="w-5 h-5 text-blue-600 shrink-0" />
                <span className="text-sm font-medium text-gray-800">{r.name}</span>
              </Link>
            ))}
          </div>
        </SectionCard>

        {/* ===== Operational Readiness (Admin/Owner only) ===== */}
        {isAdmin && (
          <SectionCard data-testid="hub-readiness" className="border-blue-100 bg-blue-50/40">
            <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-3">
              <ClipboardCheck className="w-6 h-6 text-blue-600" /> Operational Readiness
              <span className="text-[11px] font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">Admin / Owner</span>
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              Phase II readiness deliverables (June 2026). Single source of truth — governance documents (not published to drivers).
              Verdict: <span className="font-semibold text-gray-700">Ready with owner actions</span> · Soft Go-Live not authorized.
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {READINESS_DOCS.map((d) => (
                <div key={d.name} data-testid={`hub-readiness-${d.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  className="flex items-start gap-3 rounded-lg border border-blue-200 bg-white px-4 py-3">
                  <FileText className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-gray-800 block">{d.name}</span>
                    <span className="text-[11px] text-gray-400 break-all">{d.path}</span>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* ===== 5. Website Integration / Ecosystem ===== */}
        <SectionCard data-testid="hub-ecosystem">
          <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-3">
            <ExternalLink className="w-6 h-6 text-blue-600" /> One Connected Ecosystem
          </h2>
          <p className="text-sm text-gray-500 mb-5">Move seamlessly between the website and portal.</p>
          <div className="flex flex-wrap gap-3">
            {ECOSYSTEM.map((e) => (
              <Link key={e.name} to={e.to} data-testid={`hub-eco-${e.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 text-white px-4 py-2 text-sm font-semibold hover:bg-blue-700 transition-colors">
                <e.icon className="w-4 h-4" /> {e.name}
              </Link>
            ))}
          </div>
        </SectionCard>

        {/* ===== 6. News & Announcements ===== */}
        <SectionCard data-testid="hub-announcements">
          <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-3">
            <Megaphone className="w-6 h-6 text-blue-600" /> News &amp; Announcements
          </h2>
          {announcements.length === 0 ? (
            <div className="mt-4 rounded-xl border-2 border-dashed border-gray-200 p-10 text-center" data-testid="hub-announcements-empty">
              <Megaphone className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">No announcements yet</p>
              <p className="text-sm text-gray-400 mt-1">Company updates and news will appear here as they are published.</p>
            </div>
          ) : (
            <div className="mt-4 space-y-4" data-testid="hub-announcements-list">
              {announcements.map((a) => (
                <div key={a.id} className="border-l-4 border-blue-500 pl-4" data-testid={`hub-announcement-${a.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">{fmtType(a.comm_type)}</span>
                    {a.featured && <span className="text-[11px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Featured</span>}
                    <span className="text-xs text-gray-400">{a.published_at ? new Date(a.published_at).toLocaleDateString() : ''}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mt-1">{a.title}</h3>
                  <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{a.body}</p>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* ===== Company Timeline ===== */}
        <SectionCard data-testid="hub-timeline">
          <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-3">
            <History className="w-6 h-6 text-blue-600" /> Company Timeline
          </h2>
          <p className="text-sm text-gray-500 mb-4">BlueLock's historical archive of milestones and achievements.</p>
          {timeline.length === 0 ? (
            <div className="mt-2 rounded-xl border-2 border-dashed border-gray-200 p-10 text-center" data-testid="hub-timeline-empty">
              <History className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">Timeline coming soon</p>
              <p className="text-sm text-gray-400 mt-1">Company milestones will be recorded here as BlueLock grows.</p>
            </div>
          ) : (
            <ol className="mt-2 relative border-l-2 border-blue-100 ml-2" data-testid="hub-timeline-list">
              {timeline.map((t) => (
                <li key={t.id} className="ml-6 mb-6" data-testid={`hub-timeline-${t.id}`}>
                  <span className="absolute -left-[9px] w-4 h-4 rounded-full bg-blue-600 border-2 border-white" />
                  <span className="text-xs text-gray-400">{t.event_date}</span>
                  <span className="ml-2 text-[11px] font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">{fmtType(t.category)}</span>
                  <h3 className="font-semibold text-gray-900 mt-1">{t.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{t.description}</p>
                </li>
              ))}
            </ol>
          )}
        </SectionCard>
      </div>
    </DashboardLayout>
  );
};

export default BlueLockHubPage;
